import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Subject, lastValueFrom } from 'rxjs';
import { SCOPES } from 'app/core/authorization/constants/scopes.constant';
import { SESSION_STORAGE_KEYS } from 'app/core/session_storage/constants/session-storage-keys.constant';
import { clearAllStoredAccountSelections, clearStoredAccountSelection } from 'app/core/auth/account-selection.util';
import { OrganizationContextService } from 'app/core/auth/organization-context.service';
import { AuthSwitchAttempt } from 'app/core/auth/auth-switch-attempt';

export interface AccountSwitchEvent {
  type: 'user_not_registered' | 'access_denied' | 'tenant_mismatch';
  message: string;
  tenantId?: string;
  userEmail?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccountSwitchService {
  private accountSwitchRequired$ = new Subject<AccountSwitchEvent>();
  private _isInTenantSelectionMode = false;

  /**
   * Observable that emits when a 403 error indicates the user needs to switch accounts
   */
  onAccountSwitchRequired$ = this.accountSwitchRequired$.asObservable();

  /**
   * Check if we're currently in tenant selection mode
   * When true, 403 errors should be ignored to prevent dialog loops
   */
  get isInTenantSelectionMode(): boolean {
    return this._isInTenantSelectionMode;
  }

  /**
   * Set tenant selection mode
   * @param value true when entering tenant selection, false when tenant is selected
   */
  setTenantSelectionMode(value: boolean): void {
    this._isInTenantSelectionMode = value;
    if (value) this.organizationContext.clear();
  }

  constructor(
    private msalService: MsalService,
    private organizationContext: OrganizationContextService,
  ) {}

  /**
   * Trigger the account switch dialog when a 403 is detected
   */
  triggerAccountSwitch(event: AccountSwitchEvent): void {
    this.accountSwitchRequired$.next(event);
  }

  /**
   * Get current account info for display in the dialog
   */
  getCurrentAccountInfo(): { email: string; tenantId: string } | null {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount) {
      return null;
    }
    return {
      email: activeAccount.username,
      tenantId: activeAccount.tenantId,
    };
  }

  /**
   * Get all available accounts in the cache.
   */
  getAllAccounts(): Array<{ email: string; tenantId: string; name: string }> {
    return this.msalService.instance.getAllAccounts().map((account) => ({
      email: account.username,
      tenantId: account.tenantId,
      name: account.name || account.username,
    }));
  }

  /**
   * Login with account picker prompt - forces user to select an account
   * IMPORTANT: We must clear the current account from cache first, otherwise
   * MSAL will auto-select the cached account even with prompt: 'select_account'
   */
  async loginWithAccountPicker(): Promise<void> {
    const storedTenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
    const attempt = new AuthSwitchAttempt(
      this.msalService,
      this.organizationContext,
      storedTenantId ?? 'organizations',
    );
    const authority = storedTenantId
      ? `https://login.microsoftonline.com/${storedTenantId}/`
      : 'https://login.microsoftonline.com/organizations/';

    try {
      // CRITICAL: Clear the current active account from cache
      // This prevents MSAL from auto-selecting it after the account picker
      const activeAccount = this.msalService.instance.getActiveAccount();
      if (activeAccount) {
        // Remove only the active account, keep others for selection
        await this.msalService.instance.clearCache({ account: activeAccount });
        attempt.clearSelection(storedTenantId ?? activeAccount.tenantId);
        this.msalService.instance.setActiveAccount(null);
      }

      // Use prompt: 'select_account' to force account picker
      // With the old account cleared, user MUST select a different one
      attempt.assertCurrent();
      await lastValueFrom(
        this.msalService.loginRedirect({
          scopes: [SCOPES.USER_IMPERSONATION],
          authority: authority,
          prompt: 'select_account',
        }),
        { defaultValue: undefined },
      );
      attempt.assertCurrent();
    } catch (error) {
      attempt.recover();
      throw error;
    }
  }

  /**
   * Login with fresh credentials - forces complete re-authentication
   * Clears ALL cached accounts to ensure a completely fresh login
   */
  async loginWithFreshCredentials(): Promise<void> {
    const storedTenantId = localStorage.getItem(SESSION_STORAGE_KEYS.TENANT_ID);
    const attempt = new AuthSwitchAttempt(
      this.msalService,
      this.organizationContext,
      storedTenantId ?? 'organizations',
    );
    const authority = storedTenantId
      ? `https://login.microsoftonline.com/${storedTenantId}/`
      : 'https://login.microsoftonline.com/organizations/';

    try {
      // Clear ALL accounts from cache for a truly fresh login
      const allAccounts = this.msalService.instance.getAllAccounts();

      for (const account of allAccounts) {
        attempt.assertCurrent();
        await this.msalService.instance.clearCache({ account });
        attempt.clearSelection(account.tenantId);
      }
      this.msalService.instance.setActiveAccount(null);

      // Use prompt: 'login' to force fresh login with credentials
      await lastValueFrom(
        this.msalService.loginRedirect({
          scopes: [SCOPES.USER_IMPERSONATION],
          authority: authority,
          prompt: 'login',
        }),
        { defaultValue: undefined },
      );
      attempt.assertCurrent();
    } catch (error) {
      attempt.recover();
      throw error;
    }
  }

  /**
   * Logout current account and clear cache for clean re-login
   * This does a FULL logout from Microsoft and clears all local state
   */
  async logoutAndRelogin(): Promise<void> {
    this.organizationContext.clear();
    // Clear stored tenant to allow fresh selection
    clearAllStoredAccountSelections();
    localStorage.removeItem(SESSION_STORAGE_KEYS.TENANT_ID);
    localStorage.removeItem('currentUser');

    // Clear all accounts from MSAL cache
    const accounts = this.msalService.instance.getAllAccounts();
    for (const account of accounts) {
      await this.msalService.instance.clearCache({ account });
    }
    this.msalService.instance.setActiveAccount(null);

    // Do a proper MSAL logout which will redirect to Microsoft logout
    // After logout, user will be redirected back and need to login fresh
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
  }
}
